import { Component, Input, OnInit } from '@angular/core';
import * as _ from 'lodash';

import { WikiService } from '../services/wiki.service';
import { SiteConfigService } from '../../../core/services/core.site-config.service';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';

/**
 * Download control for a wiki page, in any section.
 *
 * Every wiki surface has one of these: the project wiki toolbar, the versions
 * tab, and the notebooks that hang off tracker items and personal user space.
 * They are all wiki pages and all go through the same endpoint, so the logic
 * lives here once rather than being copied into each host — the host supplies
 * the page's scope, optionally the version to pin, and the button styling that
 * matches its own toolbar.
 */
@Component({
    selector: 'wiki-download',
    template: require('./wiki.download.component.html')
})
export class WikiDownloadComponent implements OnInit {
    /** Page name or numeric id — the endpoint accepts either. */
    @Input() pagename: string = '';
    /** Wiki section: 'project', 'user', 'trackeritem', … */
    @Input() section: string = 'project';
    /** Owning record id, for the non-project sections. */
    @Input() refId: any = null;
    /** Project unixName, for the project section. */
    @Input() unixName: string = '';
    /**
     * Page version to download. Null downloads the latest, which is what a page
     * view wants; the versions tab pins this to the version on screen.
     */
    @Input() version: number | null = null;
    /** Button classes, so the control matches the toolbar it sits in. */
    @Input() buttonClass: string = 'btn btn-trans';

    /** Formats a converter is actually running for; empty hides the menu. */
    exportFormats: string[] = [];
    /** Format currently being generated, so the button can show progress. */
    downloading: string = '';

    constructor(
        private wikiService: WikiService,
        private siteConfigService: SiteConfigService,
        private uiService: UiService,
        private gfTranslateService: GfTranslateService,
    ) {}

    ngOnInit(): void {
        this.exportFormats =
            _.get(this.siteConfigService.getSiteConfig(), 'documentExportFormats', []) || [];
    }

    /** Whether anything can be offered at all. */
    get available(): boolean {
        return !!this.pagename && this.exportFormats.length > 0;
    }

    /** What the button says it does, which depends on whether a version is pinned. */
    get tooltip(): string {
        return this.gfTranslateService.translate(this.version
            ? 'Download this version as a document'
            : 'Download this page as a document');
    }

    /** Whether the backend advertises a converter that can produce this format. */
    canExport(format: string): boolean {
        return this.exportFormats.indexOf(format) !== -1;
    }

    /**
     * Download the page as PDF or Word.
     *
     * The backend generates and streams the file in one request, so this can take
     * a second or two — hence the in-flight flag rather than a plain link. The blob
     * is handed to the browser through a temporary object URL, which is revoked
     * immediately afterwards so it cannot pin the file in memory.
     */
    download(format: 'pdf' | 'docx'): void {
        if (this.downloading) { return; }
        this.downloading = format;

        this.wikiService.exportPage(this.pagename, format, this.exportParams())
            .then((blob: Blob) => this.saveBlob(blob, format))
            .catch(() => this.uiService.message.showError(
                this.gfTranslateService.translate('The document could not be generated.')))
            .then(() => { this.downloading = ''; });
    }

    /** The route scope, plus the version when one is pinned. */
    private exportParams(): any {
        const params = this.scopeParams();
        if (this.version) { params.version = this.version; }
        return params;
    }

    /**
     * The scope the endpoint needs to resolve a page *name*.
     *
     * `project` and `section`/`refId` are mutually exclusive: the backend gives
     * `project` precedence and forces section='project' with it, which would resolve
     * the wrong page for a notebook hosted on a tracker item inside a project.
     */
    private scopeParams(): any {
        if (this.section === 'project' && this.unixName) {
            return { project: this.unixName };
        }
        return { section: this.section, refId: this.refId };
    }

    /**
     * Hand a generated document to the browser as a download.
     *
     * The name is built here rather than read from Content-Disposition: a blob URL
     * carries no headers. A pinned version goes into the name, as the backend does
     * it, so two versions of one page do not overwrite each other on disk.
     */
    private saveBlob(blob: Blob, format: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.version
            ? `${this.pagename}-v${this.version}.${format}`
            : `${this.pagename}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}
